<template>
  <div class="user-detail-container mt-5 pt-5">
    <div class="container pt-5 px-3">
      <!-- Botón de volver -->
      <div class="mb-4">
        <router-link to="/admin/usuarios" class="btn btn-outline-light">
          <i class="fas fa-arrow-left me-2"></i> Volver a la lista
        </router-link>
      </div>

      <!-- Loader -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p class="mt-2">Cargando información del usuario...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="alert alert-danger">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
      </div>

      <div v-else class="row">
        <!-- Información del usuario -->
        <div class="col-lg-4 mb-4">
          <div class="card user-info-card">
            <div class="card-header">
              <h2 class="card-title">Información del Usuario</h2>
            </div>
            <div class="card-body">
              <div class="user-profile-header">
                <div class="user-avatar">
                  <i class="fas fa-user"></i>
                </div>
                <div class="user-main-info">
                  <h3 class="user-name">{{ userData.user.first_name }} {{ userData.user.last_name }}</h3>
                  <p class="user-username">@{{ userData.user.username }}</p>
                  <div class="user-badge" :class="{'admin-badge': userData.user.is_staff}">
                    {{ userData.user.is_staff ? 'Administrador' : 'Usuario' }}
                  </div>
                </div>
              </div>

              <div class="user-details">
                <div class="detail-item">
                  <div class="detail-label">
                    <i class="fas fa-envelope"></i>
                    <span>Email</span>
                  </div>
                  <div class="detail-value">{{ userData.user.email }}</div>
                </div>
                <div class="detail-item" v-if="userData.user.instagram">
                  <div class="detail-label">
                    <i class="fab fa-instagram"></i>
                    <span>Instagram</span>
                  </div>
                  <div class="detail-value">{{ userData.user.instagram }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">
                    <i class="fas fa-id-card"></i>
                    <span>ID de Usuario</span>
                  </div>
                  <div class="detail-value">{{ userData.user.id }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">
                    <i class="fas fa-shopping-bag"></i>
                    <span>Total de Pedidos</span>
                  </div>
                  <div class="detail-value">{{ userData.orders.length }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pedidos del usuario -->
        <div class="col-lg-8">
          <div class="card orders-card">
            <div class="card-header">
              <h2 class="card-title">Pedidos del Usuario</h2>
            </div>
            <div class="card-body p-0">
              <div v-if="userData.orders.length === 0" class="empty-state">
                <i class="fas fa-shopping-cart fa-3x mb-3"></i>
                <h3>Sin pedidos</h3>
                <p>Este usuario aún no ha realizado ningún pedido.</p>
              </div>
              <div v-else class="orders-list">
                <div v-for="order in userData.orders" :key="order.id" class="order-item">
                  <div class="order-header">
                    <div class="order-id">
                      <i class="fas fa-shopping-bag me-2"></i>
                      Pedido #{{ order.id }}
                    </div>
                    <div class="order-date">
                      <i class="fas fa-calendar-alt me-1"></i>
                      {{ formatDate(order.created_at) }}
                    </div>
                    <div>
                      <div class="order-status d-flex" :class="`status-${order.status}`">
                        {{ order.status_display }}
                      </div>
                    </div>
                  </div>
                  <div class="order-body">
                    <div class="order-products">
                      <div v-for="product in order.order_products" :key="product.id" class="product-item">
                        <div class="product-imagee" style="width: 50px !important; height: 50px !important;">
                          <img :src="product.product_image || '@/assets/images/logo.png'" alt="Producto">
                        </div>
                        <div class="product-info">
                          <div class="product-name">{{ product.product_name }}</div>
                          <div class="product-quantity">
                            {{ product.quantity }} x {{ formatPrice(product.price_at_time) }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="order-total">
                      <span>Total:</span>
                      <span class="total-amount">{{ formatPrice(order.total) }}</span>
                    </div>
                  </div>
                  <div class="order-footer">
                    <router-link :to="`/admin/pedidos/${order.id}`" class="btn btn-primary btn-sm">
                      <i class="fas fa-eye me-1"></i> Ver detalles
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { getUserDetails } from '@/services/auth';
import apiClient from '@/services/apiClient';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const loading = ref(true);
const error = ref(null);
const userData = ref({
  user: {},
  orders: []
});

// Función para buscar un usuario por email
const getUserByEmail = async (email) => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) throw new Error('No hay sesión activa');
    
    const response = await apiClient.get(`/users/?email=${encodeURIComponent(email)}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Suponiendo que la API devuelve una lista de usuarios que coinciden con el email
    if (response.data && response.data.length > 0) {
      return response.data[0]; // Tomar el primer usuario encontrado
    } else if (response.data && response.data.results && response.data.results.length > 0) {
      return response.data.results[0]; // Para APIs paginadas
    }
    
    return null;
  } catch (err) {
    console.error('Error buscando usuario por email:', err);
    toast.error('Error al buscar el usuario por email');
    return null;
  }
};

// Formatear fecha
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Formatear precio
const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(price);
};

// Cargar datos del usuario
const loadUserData = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    // Verificar primero si hay un parámetro de email en la URL
    const emailParam = route.query.email;
    let userId = route.params.id;
    
    // Si hay email en la URL pero no ID en la ruta, buscar por email
    if (emailParam && !userId) {
      console.log('Buscando usuario por email:', emailParam);
      // La API deberá implementar una búsqueda por email
      // Por ahora simulamos con un endpoint que acepte email como query param
      const userResponse = await getUserByEmail(emailParam);
      if (userResponse && userResponse.id) {
        userId = userResponse.id;
      } else {
        throw new Error('No se encontró un usuario con ese email');
      }
    }
    
    if (!userId) {
      throw new Error('No se proporcionó un ID de usuario válido');
    }
    
    // Una vez que tenemos el userId, obtenemos los detalles
    const response = await getUserDetails(userId);
    userData.value = response;
  } catch (err) {
    console.error('Error al cargar detalles del usuario:', err);
    error.value = 'Error al cargar la información del usuario. Por favor, inténtalo de nuevo.';
    toast.error(error.value);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  try {
    await loadUserData();
    
    // Si estábamos buscando por email y encontramos un usuario, actualizar la URL con el ID
    if (route.query.email && userData.value.user && userData.value.user.id) {
      // Actualizar la URL sin recargar la página
      router.replace({
        name: 'UserDetail',
        params: { id: userData.value.user.id },
        query: {} // Eliminar los query params
      });
    }
  } catch (err) {
    console.error('Error en onMounted:', err);
    // Si hay un error crítico, redirigir a la lista de usuarios
    if (!userData.value.user || !userData.value.user.id) {
      toast.error('No se pudo cargar el usuario, regresando a la lista');
      setTimeout(() => {
        router.push('/admin/usuarios');
      }, 2000);
    }
  }
});
</script>

<style scoped>
.user-detail-container {
  min-height: 100vh;
  background: var(--black-color);
  padding-bottom: 3rem;
}

.card {
  background: var(--black-color-3);
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.card-header {
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.25rem 1.5rem;
}

.card-title {
  color: var(--white-color);
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.user-profile-header {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
}

.user-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  color: white;
  font-size: 2.5rem;
}

.user-main-info {
  flex: 1;
}

.user-name {
  color: var(--white-color);
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.user-username {
  color: var(--white-color-2);
  margin: 0.25rem 0 0.5rem;
  font-size: 1rem;
}

.user-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(108, 117, 125, 0.2);
  color: #6c757d;
}

.admin-badge {
  background: rgba(var(--primary-color-rgb), 0.2);
  color: var(--primary-color);
}

.user-details {
  margin-top: 1.5rem;
}

.detail-item {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.detail-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.detail-label {
  display: flex;
  align-items: center;
  color: var(--white-color-2);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.detail-label i {
  width: 20px;
  margin-right: 0.75rem;
}

.detail-value {
  color: var(--white-color);
  font-size: 1rem;
  font-weight: 500;
  word-break: break-word;
}

.orders-list {
  max-height: 600px;
  overflow-y: auto;
}

.order-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.order-item:last-child {
  border-bottom: none;
}

.order-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.1);
}

.order-id {
  color: var(--white-color);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.order-date {
  color: var(--white-color-2);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.order-status {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-pending {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-completed {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-cancelled {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.order-body {
  padding: 1.5rem;
}

.product-item {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.product-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.product-imagee {
  width: 50px;
  height: 50px;
  min-width: 50px;
  min-height: 50px;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 1rem;
  background: var(--black-color-2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-imagee img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-info {
  flex: 1;
}

.product-name {
  color: var(--white-color);
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.product-quantity {
  color: var(--white-color-2);
  font-size: 0.9rem;
}

.order-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
  font-weight: 600;
}

.total-amount {
  color: var(--primary-color);
  font-size: 1.1rem;
}

.order-footer {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.1);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--white-color-2);
  text-align: center;
}

/* Media queries para dispositivos móviles */
@media (max-width: 767px) {
  .user-profile-header {
    flex-direction: column;
    text-align: center;
  }
  
  .user-avatar {
    margin-right: 0;
    margin-bottom: 1rem;
  }
  
  .order-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .order-id, .order-date, .order-status {
    margin-bottom: 0.5rem;
    width: 100%;
  }
  
  .order-status {
    align-self: flex-start;
  }
  
  .card-title {
    font-size: 1.1rem;
  }
  
  .user-name {
    font-size: 1.3rem;
  }
  
  .product-name {
    font-size: 0.9rem;
  }
  
  .product-quantity {
    font-size: 0.8rem;
  }
  
  .order-total {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .total-amount {
    margin-top: 0.5rem;
  }
}
</style> 